import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import type * as React from "react";

import { cn } from "@/lib/utils";

const tapScaleClass = "active:not-disabled:scale-[0.96]";

const buttonVariants = cva(
	[
		"inline-flex items-center justify-center gap-2 whitespace-nowrap",
		"rounded-[var(--radius-control)] border border-transparent",
		"text-[0.8125rem] md:text-[15px] font-normal",
		"transition-[color,background-color,border-color,outline-color,transform] duration-[var(--motion-fast)] ease-[var(--motion-ease)]",
		"disabled:pointer-events-none disabled:opacity-50 disabled:cursor-not-allowed",
		"[&_svg]:pointer-events-none [&_svg]:shrink-0",
		"max-md:min-h-11",
		"outline-none cursor-pointer",
		"focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-black dark:focus-visible:outline-white",
		"aria-invalid:outline-danger aria-invalid:outline-2",
	].join(" "),
	{
		variants: {
			variant: {
				default:
					"border-black rounded-full bg-black text-[var(--color-off-white,#F6F5F3)] hover:bg-black/85 dark:border-white dark:bg-white dark:text-black dark:hover:bg-white/85",
				brand:
					"border-black rounded-full bg-black text-[var(--color-off-white,#F6F5F3)] hover:bg-black/85 dark:border-white dark:bg-white dark:text-black",
				destructive:
					"border-danger bg-danger text-white hover:bg-danger/90 focus-visible:outline-danger",
				outline:
					"border-black/10 rounded-full bg-transparent text-content-primary hover:bg-black/3 dark:border-white/10 dark:hover:bg-white/3",
				secondary:
					"border-black rounded-full bg-transparent text-content-primary hover:bg-black/3 dark:border-white dark:bg-transparent dark:text-content-primary dark:hover:bg-white/3",
				ghost:
					"border-transparent bg-transparent text-content-primary hover:bg-black/3 dark:hover:bg-white/3 rounded-full",
				link: "h-auto border-transparent bg-transparent p-0 text-content-primary underline-offset-4 hover:underline rounded-full",
			},
			size: {
				xs: "gap-1.5 px-2.5 py-1.5 text-xs leading-4 has-[>svg]:px-2",
				default: "px-[18px] py-2 leading-5 has-[>svg]:px-4",
				sm: "px-3.5 py-1.5 text-xs leading-4 has-[>svg]:px-3",
				lg: "px-6 py-2.5 leading-5 has-[>svg]:px-5",
				pill: "rounded-full px-4 py-2 text-xs font-light leading-5",
				icon: "aspect-square p-3.5 leading-none",
				"icon-sm": "aspect-square p-2.5 leading-none",
				"icon-lg": "aspect-square p-3.5 leading-none",
			},
		},
		defaultVariants: {
			variant: "default",
			size: "default",
		},
	},
);

function Button({
	className,
	variant,
	size,
	asChild = false,
	static: isStatic = false,
	...props
}: React.ComponentPropsWithoutRef<"button"> &
	VariantProps<typeof buttonVariants> & {
		asChild?: boolean;
		/** Disable press scale when motion would be distracting. */
		static?: boolean;
	}) {
	const Comp = asChild ? Slot : "button";

	return (
		<Comp
			data-slot="button"
			className={cn(
				buttonVariants({ variant, size }),
				!isStatic && tapScaleClass,
				className,
			)}
			{...props}
		/>
	);
}

export { Button, buttonVariants };
